import { useEffect, useState } from 'react';
import type { ContainerKind, IdPreview } from './types';

interface IdPreviewPopoverProps {
  container: ContainerKind;
  id: string;
  fetchPreview: (container: ContainerKind, id: string) => Promise<IdPreview | null>;
  onViewJson?: () => void;
  /** Anchor position in viewport coords. */
  anchorRect: { left: number; top: number; bottom: number };
}

export function IdPreviewPopover({
  container,
  id,
  fetchPreview,
  onViewJson,
  anchorRect,
}: IdPreviewPopoverProps) {
  const [preview, setPreview] = useState<IdPreview | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setPreview(undefined);
    fetchPreview(container, id)
      .then(p => { if (!cancelled) setPreview(p); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [container, id, fetchPreview]);

  // Position below the trigger; flip up if no room
  const POPOVER_HEIGHT_ESTIMATE = 200;
  const flipUp = anchorRect.bottom + POPOVER_HEIGHT_ESTIMATE > window.innerHeight;
  const style: React.CSSProperties = flipUp
    ? { left: anchorRect.left, bottom: window.innerHeight - anchorRect.top + 4 }
    : { left: anchorRect.left, top: anchorRect.bottom + 4 };

  return (
    <div className="id-control-preview" style={style} onClick={e => e.stopPropagation()}>
      {preview === undefined && <div className="id-control-preview-status">Loading…</div>}
      {preview === null && <div className="id-control-preview-status">Not found</div>}
      {preview && (
        <>
          <div className="id-control-preview-title">{preview.title}</div>
          {preview.fields.map(f => (
            <div key={f.label} className="id-control-preview-row">
              <span className="id-control-preview-field">{f.label}:</span>{' '}
              <span className="id-control-preview-value">{f.value}</span>
            </div>
          ))}
          {onViewJson && (
            <button className="id-control-preview-jsonlink" onClick={onViewJson}>
              View full JSON →
            </button>
          )}
        </>
      )}
    </div>
  );
}
